import { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { expValEquals } from '@confluence/feature-experiments';
import { useRightSidebarContext } from '@confluence/page-layout-context';

import { APPROVALS_ANALYTICS_SOURCE } from './analytics.ts';
import { useApprovals } from './ApprovalsContext.tsx';
import {
	APPROVALS_PANE_CURRENT_CONTENT_STATUS,
	openApprovalsPaneForContext,
} from './approvalsPaneSidebar.ts';

const OVERFLOW_MENU_ENTRY_POINT = 'overflowMenu';

type UseStartApprovalOverflowMenuItemParams = {
	/** Content type from the host header — the entry point is offered on pages only */
	contentType: string | undefined;
	/** Content mode from the host header — the entry point is offered on the published page only */
	contentMode: string | undefined;
	/** Whether the viewer can update this content */
	hasUpdatePermission: boolean;
	/** Archived pages cannot start a review */
	isArchived: boolean;
	/** Anonymous viewers cannot start a review */
	isLoggedIn: boolean;
};

type UseStartApprovalOverflowMenuItemResult = {
	/** Whether the host menu should render the Start approval item */
	shouldRender: boolean;
	/** Opens the approvals pane on the published page in start-approval mode */
	onStartApproval: () => void;
};

/**
 * Drives the "Start approval" item in the page overflow (kebab) menu.
 *
 * Unlike the byline and share-dialog entry points, this one is offered even when the space does
 * not require approval before publishing — it only needs approvals to be enabled for the space.
 * It is hidden once a review already exists on the published page, because the byline and the
 * approvals pane own that state.
 *
 * The caller owns `shouldRender` (rather than the item rendering `null` itself) so the overflow
 * menu's separator logic keeps counting real items.
 */
export const useStartApprovalOverflowMenuItem = ({
	contentType,
	contentMode,
	hasUpdatePermission,
	isArchived,
	isLoggedIn,
}: UseStartApprovalOverflowMenuItemParams): UseStartApprovalOverflowMenuItemResult => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { approvalRequest, contentId, isContentApprovalsEnabled } = useApprovals();
	const rightSidebarContext = useRightSidebarContext();

	const hasActiveReview =
		Boolean(approvalRequest?.review) && approvalRequest?.review?.reviewStatus !== 'CANCELED';

	const shouldRender =
		contentType === 'page' &&
		contentMode === 'view' &&
		isLoggedIn &&
		!isArchived &&
		hasUpdatePermission &&
		isContentApprovalsEnabled &&
		!hasActiveReview &&
		expValEquals('confluence_approvals_eap', 'isEnabled', true) &&
		expValEquals('confluence_approvals_entry_point_updates_exp', 'isEnabled', true);

	const onStartApproval = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'startApproval',
				source: APPROVALS_ANALYTICS_SOURCE,
				attributes: {
					contentId,
					trigger: OVERFLOW_MENU_ENTRY_POINT,
				},
			},
		}).fire();

		openApprovalsPaneForContext({
			contentId,
			contentStatus: APPROVALS_PANE_CURRENT_CONTENT_STATUS,
			entryPoint: OVERFLOW_MENU_ENTRY_POINT,
			rightSidebarContext,
		});
	}, [contentId, createAnalyticsEvent, rightSidebarContext]);

	return { shouldRender, onStartApproval };
};
